/**
 * Session Manager
 * Tracks active device/browser sessions for concurrent access.
 * Uses Firestore for cross-device session registry and BroadcastChannel
 * for same-browser tab coordination (leader election).
 *
 * Firestore path: users/{uid}/sessions/{sessionId}
 */

import { getFirestore, doc, setDoc, deleteDoc, getDocs, collection, onSnapshot, query, where, Unsubscribe } from 'firebase/firestore';
import { getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { DeviceSession, SESSION_LIMIT } from '../types';
import { logger } from './logger';
import { isFirestoreWriteAvailable, markFirestoreWriteDenied, markFirestoreWriteOk } from './storage';

// --- Helpers ---

function getFirebaseApp() {
  const apps = getApps();
  return apps.length > 0 ? apps[0] : null;
}

function getDb() {
  const app = getFirebaseApp();
  if (!app) return null;
  return getFirestore(app);
}

/** Generate a unique session ID */
function generateSessionId(): string {
  return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/** Get or create a stable tab ID for this browser tab (survives page reloads) */
function getTabId(): string {
  let tabId = sessionStorage.getItem('canvas-tab-id');
  if (!tabId) {
    tabId = `tab_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('canvas-tab-id', tabId);
  }
  return tabId;
}

/** Parse user agent to extract device info */
function parseUserAgent(): { browser: string; os: string; deviceType: 'desktop' | 'mobile' | 'tablet'; deviceName: string } {
  const ua = navigator.userAgent;

  // Detect browser
  let browser = 'Unknown';
  if (ua.includes('Firefox/')) {
    const v = ua.match(/Firefox\/(\d+)/);
    browser = `Firefox ${v?.[1] || ''}`.trim();
  } else if (ua.includes('Edg/')) {
    const v = ua.match(/Edg\/(\d+)/);
    browser = `Edge ${v?.[1] || ''}`.trim();
  } else if (ua.includes('Chrome/')) {
    const v = ua.match(/Chrome\/(\d+)/);
    browser = `Chrome ${v?.[1] || ''}`.trim();
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    const v = ua.match(/Version\/(\d+)/);
    browser = `Safari ${v?.[1] || ''}`.trim();
  }

  // Detect OS
  let os = 'Unknown';
  if (ua.includes('Mac OS X')) {
    const v = ua.match(/Mac OS X (\d+[._]\d+)/);
    os = `macOS ${v?.[1]?.replace(/_/g, '.') || ''}`.trim();
  } else if (ua.includes('Windows NT')) {
    const v = ua.match(/Windows NT (\d+\.\d+)/);
    const winVersion: Record<string, string> = { '10.0': '10/11', '6.3': '8.1', '6.2': '8', '6.1': '7' };
    os = `Windows ${winVersion[v?.[1] || ''] || v?.[1] || ''}`.trim();
  } else if (ua.includes('Linux')) {
    os = ua.includes('Android') ? 'Android' : 'Linux';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    const v = ua.match(/OS (\d+_\d+)/);
    os = `iOS ${v?.[1]?.replace(/_/g, '.') || ''}`.trim();
  } else if (ua.includes('CrOS')) {
    os = 'ChromeOS';
  }

  // Detect device type
  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/iPad|tablet/i.test(ua) || (ua.includes('Android') && !ua.includes('Mobile'))) {
    deviceType = 'tablet';
  } else if (/iPhone|iPod|Android.*Mobile|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua)) {
    deviceType = 'mobile';
  }

  const deviceName = `${browser} on ${os}`;

  return { browser, os, deviceType, deviceName };
}

// --- Session Manager Class ---

export class SessionManager {
  private userId: string;
  private sessionId: string;
  private tabId: string;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private isLeader: boolean = false;
  private broadcastChannel: BroadcastChannel | null = null;
  private unsubscribeCurrentSession: Unsubscribe | null = null;
  private onSessionRevoked: (() => void) | null = null;
  private isRegistered: boolean = false;
  private deviceInfo: ReturnType<typeof parseUserAgent>;

  constructor(userId: string) {
    this.userId = userId;
    this.sessionId = generateSessionId();
    this.tabId = getTabId();
    this.deviceInfo = parseUserAgent();
  }

  /** Get the current session ID */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Register this tab as an active session.
   * Uses BroadcastChannel leader election so only one tab per browser
   * writes to Firestore. Other tabs share the leader's session.
   */
  async register(onRevoked?: () => void): Promise<{ success: boolean; error?: string }> {
    this.onSessionRevoked = onRevoked || null;

    // Set up BroadcastChannel for cross-tab coordination
    try {
      this.broadcastChannel = new BroadcastChannel(`session-sync-${this.userId}`);
      this.broadcastChannel.onmessage = (event) => this.handleBroadcastMessage(event);
    } catch (e) {
      // BroadcastChannel not supported — each tab acts independently
      logger.warn('SessionManager', 'BroadcastChannel not supported, each tab will register independently');
    }

    // Attempt leader election: first tab that responds is leader
    this.isLeader = await this.electLeader();

    if (!this.isLeader) {
      // Non-leader tab — still track locally but don't write to Firestore
      logger.debug('SessionManager', `Tab ${this.tabId} is follower — leader handles Firestore session`);
      this.listenForRevocation();
      return { success: true };
    }

    // Leader tab: check session limit and register in Firestore
    const db = getDb();
    if (!db) {
      return { success: false, error: 'Firebase not initialized' };
    }

    // Skip if a previous Firestore write already failed with permission-denied
    if (isFirestoreWriteAvailable() === false) {
      logger.debug('SessionManager', 'Firestore writes disabled, skipping session registration');
      return { success: false, error: 'Firestore not ready' };
    }

    // Guard: Ensure Firebase Auth token is propagated to Firestore SDK
    const app = getFirebaseApp();
    if (app) {
      const auth = getAuth(app);
      if (!auth.currentUser) {
        return { success: false, error: 'Firebase Auth not ready' };
      }
      try { await auth.currentUser.getIdToken(); } catch { return { success: false, error: 'Firebase Auth not ready' }; }
    }

    try {
      // Count active sessions
      const sessionsRef = collection(db, 'users', this.userId, 'sessions');
      const snapshot = await getDocs(sessionsRef);
      const activeSessions = snapshot.docs.filter(d => d.data().isActive !== false);

      if (activeSessions.length >= SESSION_LIMIT) {
        // Prune stale sessions (inactive for > 24h)
        const now = Date.now();
        const staleThreshold = 24 * 60 * 60 * 1000; // 24 hours
        let pruned = 0;

        for (const sessionDoc of activeSessions) {
          const data = sessionDoc.data() as DeviceSession;
          if (now - data.lastActiveAt > staleThreshold) {
            await deleteDoc(sessionDoc.ref);
            pruned++;
          }
        }

        // Re-check after pruning
        const remainingActive = activeSessions.length - pruned;
        if (remainingActive >= SESSION_LIMIT) {
          return {
            success: false,
            error: `Maximum ${SESSION_LIMIT} devices reached. Remove a device from Settings > Account to continue.`,
          };
        }
      }

      // Register this session
      const sessionData: DeviceSession = {
        sessionId: this.sessionId,
        userId: this.userId,
        deviceName: this.deviceInfo.deviceName,
        browser: this.deviceInfo.browser,
        os: this.deviceInfo.os,
        deviceType: this.deviceInfo.deviceType,
        createdAt: Date.now(),
        lastActiveAt: Date.now(),
        isActive: true,
        tabId: this.tabId,
        isLeaderTab: true,
      };

      await setDoc(doc(db, 'users', this.userId, 'sessions', this.sessionId), sessionData);
      this.isRegistered = true;
      markFirestoreWriteOk();

      logger.info('SessionManager', `Session registered: ${this.sessionId} (${this.deviceInfo.deviceName})`);

      // Start heartbeat
      this.startHeartbeat();

      // Listen for revocation of this session
      this.listenForRevocation();

      // Notify other tabs
      this.broadcastChannel?.postMessage({
        type: 'session-registered',
        sessionId: this.sessionId,
        tabId: this.tabId,
      });

      return { success: true };
    } catch (e: any) {
      if (e.code === 'permission-denied') {
        markFirestoreWriteDenied();
        logger.warn('SessionManager', 'Firestore permission denied — session tracking disabled');
      } else {
        logger.error('SessionManager', 'Failed to register session:', e);
      }
      return { success: false, error: e.message || 'Failed to register session' };
    }
  }

  /** Elect a leader tab via BroadcastChannel (simple first-responder wins) */
  private electLeader(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.broadcastChannel) {
        // No BroadcastChannel support — this tab is always leader
        resolve(true);
        return;
      }

      let responded = false;

      const handler = (event: MessageEvent) => {
        if (event.data.type === 'leader-claim-response') {
          responded = true;
          resolve(false); // Another tab is already leader
        }
      };

      this.broadcastChannel.addEventListener('message', handler);

      // Ask if there's already a leader
      this.broadcastChannel.postMessage({ type: 'leader-claim-request', tabId: this.tabId });

      // If nobody responds within 300ms, we're the leader
      setTimeout(() => {
        this.broadcastChannel?.removeEventListener('message', handler);
        if (!responded) {
          resolve(true);
        }
      }, 300);
    });
  }

  /** Handle messages from other tabs in the same browser */
  private handleBroadcastMessage(event: MessageEvent) {
    const { type, tabId } = event.data;

    switch (type) {
      case 'leader-claim-request':
        // If we're the leader, respond to claim
        if (this.isLeader && this.isRegistered) {
          this.broadcastChannel?.postMessage({
            type: 'leader-claim-response',
            tabId: this.tabId,
            sessionId: this.sessionId,
          });
        }
        break;

      case 'session-revoked':
        // Another tab detected our session was revoked
        this.handleRevocation();
        break;
    }
  }

  /** Start periodic heartbeat to keep session alive */
  private startHeartbeat() {
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);

    this.heartbeatInterval = setInterval(async () => {
      if (!this.isRegistered || !this.isLeader) return;

      const db = getDb();
      if (!db) return;

      try {
        await setDoc(
          doc(db, 'users', this.userId, 'sessions', this.sessionId),
          { lastActiveAt: Date.now(), isActive: true },
          { merge: true }
        );
      } catch (e) {
        logger.warn('SessionManager', 'Heartbeat failed:', e);
      }
    }, 30000); // Every 30 seconds
  }

  /** Listen for this session being revoked (deleted) by another device */
  private listenForRevocation() {
    const db = getDb();
    if (!db || !this.isLeader) return;

    const sessionRef = doc(db, 'users', this.userId, 'sessions', this.sessionId);
    this.unsubscribeCurrentSession = onSnapshot(sessionRef, (snap) => {
      if (!snap.exists()) {
        // Session was deleted — we've been revoked
        logger.info('SessionManager', 'Session revoked remotely');
        this.handleRevocation();
      }
    }, (error) => {
      logger.warn('SessionManager', 'Session listener error:', error);
    });
  }

  /** Handle session revocation — notify app to sign out */
  private handleRevocation() {
    // Notify other tabs in this browser
    this.broadcastChannel?.postMessage({ type: 'session-revoked', sessionId: this.sessionId });

    // Call the revocation callback
    if (this.onSessionRevoked) {
      this.onSessionRevoked();
    }

    this.cleanup();
  }

  /**
   * Subscribe to real-time list of all sessions for this user.
   * Used by the Settings UI to show active devices.
   */
  subscribeToSessions(callback: (sessions: DeviceSession[]) => void): Unsubscribe {
    const db = getDb();
    if (!db) {
      callback([]);
      return () => {};
    }

    const sessionsRef = collection(db, 'users', this.userId, 'sessions');
    return onSnapshot(sessionsRef, (snapshot) => {
      const sessions: DeviceSession[] = snapshot.docs.map((d) => d.data() as DeviceSession);
      // Sort: current session first, then by lastActiveAt descending
      sessions.sort((a, b) => {
        if (a.sessionId === this.sessionId) return -1;
        if (b.sessionId === this.sessionId) return 1;
        return b.lastActiveAt - a.lastActiveAt;
      });
      callback(sessions);
    }, (error) => {
      logger.warn('SessionManager', 'Sessions listener error:', error);
      callback([]);
    });
  }

  /**
   * Revoke (sign out) a specific remote session.
   */
  async revokeSession(targetSessionId: string): Promise<boolean> {
    const db = getDb();
    if (!db) return false;

    try {
      await deleteDoc(doc(db, 'users', this.userId, 'sessions', targetSessionId));
      logger.info('SessionManager', `Revoked session: ${targetSessionId}`);
      return true;
    } catch (e) {
      logger.error('SessionManager', 'Failed to revoke session:', e);
      return false;
    }
  }

  /**
   * Revoke all sessions except the current one.
   */
  async revokeAllOtherSessions(): Promise<number> {
    const db = getDb();
    if (!db) return 0;

    try {
      const sessionsRef = collection(db, 'users', this.userId, 'sessions');
      const snapshot = await getDocs(sessionsRef);
      let revokedCount = 0;

      for (const sessionDoc of snapshot.docs) {
        if (sessionDoc.id !== this.sessionId) {
          await deleteDoc(sessionDoc.ref);
          revokedCount++;
        }
      }

      logger.info('SessionManager', `Revoked ${revokedCount} other sessions`);
      return revokedCount;
    } catch (e) {
      logger.error('SessionManager', 'Failed to revoke all other sessions:', e);
      return 0;
    }
  }

  /**
   * Mark session as inactive on tab close / navigation away.
   * Called from beforeunload handler.
   */
  markInactive() {
    if (!this.isRegistered || !this.isLeader) return;

    const db = getDb();
    if (!db) return;

    try {
      // Use sendBeacon pattern via fetch keepalive for reliability on tab close
      // Firestore SDK doesn't support keepalive, so we update synchronously
      setDoc(
        doc(db, 'users', this.userId, 'sessions', this.sessionId),
        { isActive: false, lastActiveAt: Date.now() },
        { merge: true }
      ).catch(() => {
        // Best-effort — tab is closing
      });
    } catch (e) {
      // Ignore errors during teardown
    }
  }

  /**
   * Fully clean up: stop heartbeat, unsubscribe listeners, close channel.
   * Call this on sign-out or component unmount.
   */
  cleanup() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.unsubscribeCurrentSession) {
      this.unsubscribeCurrentSession();
      this.unsubscribeCurrentSession = null;
    }

    if (this.broadcastChannel) {
      try {
        this.broadcastChannel.close();
      } catch (e) {
        // Ignore
      }
      this.broadcastChannel = null;
    }

    this.isRegistered = false;
    this.isLeader = false;
  }

  /**
   * Full teardown: mark inactive, delete session doc, cleanup listeners.
   * Call this on explicit sign-out.
   */
  async destroy() {
    this.cleanup();

    const db = getDb();
    if (!db) return;

    try {
      await deleteDoc(doc(db, 'users', this.userId, 'sessions', this.sessionId));
    } catch (e) {
      // Best effort
    }
  }
}

// --- Standalone subscribe function (for use without a SessionManager instance) ---

/**
 * Subscribe to sessions for a user. Used when the SessionManager
 * hasn't been initialized yet (e.g., in Settings page).
 */
export function subscribeToUserSessions(
  userId: string,
  currentSessionId: string | null,
  callback: (sessions: DeviceSession[]) => void
): Unsubscribe {
  const db = getDb();
  if (!db) {
    callback([]);
    return () => {};
  }

  const sessionsRef = collection(db, 'users', userId, 'sessions');
  return onSnapshot(sessionsRef, (snapshot) => {
    const sessions: DeviceSession[] = snapshot.docs.map((d) => d.data() as DeviceSession);
    sessions.sort((a, b) => {
      if (a.sessionId === currentSessionId) return -1;
      if (b.sessionId === currentSessionId) return 1;
      return b.lastActiveAt - a.lastActiveAt;
    });
    callback(sessions);
  }, () => {
    callback([]);
  });
}
